/**
 * CampaignsPage — the operational cockpit's campaign list.
 *
 * Campaign `auteur-cloud-commercial-v1`, task C05C. Renders what the server
 * reports about campaigns and nothing else: no client-side status inference,
 * no entitlement comparison. REQUIRED PROPERTY ("Source-local status
 * visible"): a campaign whose authoritative state lives on the owner's
 * machine carries `source_local: true` from the server, and this page shows
 * that as a visible "Source-local" badge next to the status — it never
 * papers over where the truth lives.
 *
 * Pause/resume/stop are POSTs the server owns; this page only relays them
 * and re-renders the response. Lifecycle verbs are disabled while a request
 * is in flight.
 *
 * FEDERATED TELEMETRY (campaign `auteur-campaigns-monitor-b02a-v1`, M02):
 * `GET /v1/operations/campaigns` is mounted and returns one card per
 * registered workspace campaign. Every field is rendered exactly as the
 * server sent it; a null field renders "not reported", never a guess. A
 * workspace the server could not read arrives as its own card carrying a
 * `diagnostic_note` — it is shown in the same list with that note and is
 * never hidden, never shown as healthy, and never fails the page.
 *
 * UNAVAILABLE IS HONEST: a 404 or 503 from the list endpoint renders an
 * explicit Unavailable state — never fabricated campaigns.
 *
 * PROVENANCE (campaign `auteur-owner-history-index-v1`, H02): every card shows
 * the server's `provenance.badge` verbatim — `live:<runtime>` for a campaign
 * read live from a registered workspace, `imported:<source_runtime>` for one
 * (or a timeline row) that came from the Owner History Index. The page never
 * picks a badge itself; a missing provenance renders "provenance not
 * reported". The merged timeline renders the server's aspects one by one: an
 * aspect nothing reported shows "not reported", never a filled-in value.
 * Imported cards are history, so they carry no lifecycle controls.
 *
 * BUILD STATUS: compiled and exercised as part of the Vite customer SPA.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'wouter';

import {
  OperationsApiError,
  fetchCampaigns,
  pauseCampaign,
  resumeCampaign,
  stopCampaign,
} from '../../api/operations';

const NOT_REPORTED = 'not reported';

function shown(value) {
  return value === null || value === undefined || value === '' ? NOT_REPORTED : String(value);
}

function usd(value) {
  return value === null || value === undefined ? NOT_REPORTED : '$' + String(value);
}

function gateLabel(gate) {
  return gate ? gate.gate_key + ' (' + gate.status + ')' : 'none reported';
}

/** The bounded event tail exactly as the server sent it (at most a few entries). */
export function EventTail({ events }) {
  const tail = Array.isArray(events) ? events : [];
  if (tail.length === 0) {
    return <p className="event-tail-empty">No recent events were reported.</p>;
  }
  return (
    <ol className="event-tail">
      {tail.map((event, index) => (
        <li key={index + ':' + event.at + ':' + event.event}>
          <time>{shown(event.at)}</time> <span>{event.message}</span>
        </li>
      ))}
    </ol>
  );
}

/**
 * Every telemetry field the server returns for one campaign. Shared with the
 * detail page. Values are relayed, not computed: spend, ceiling and headroom
 * are the server's figures.
 */
export function CampaignTelemetry({ campaign }) {
  const rows = [
    ['Campaign id', campaign.campaign_id],
    ['Version', [campaign.schema_version, campaign.manifest_version].filter(Boolean).join(' / ')],
    ['Round', [campaign.current_round_id, campaign.current_round_title].filter(Boolean).join(' — ')],
    [
      'Task',
      [campaign.current_task_id, campaign.current_task_title, campaign.current_task_status]
        .filter(Boolean)
        .join(' — '),
    ],
    ['Executor profile', campaign.executor_profile],
    ['Resolved model', campaign.resolved_model],
    [
      'Work order',
      [campaign.active_work_order_id, campaign.active_work_order_status].filter(Boolean).join(' — '),
    ],
    ['Spend', usd(campaign.cumulative_spend_usd)],
    ['Ceiling', usd(campaign.spend_ceiling_usd)],
    ['Headroom remaining', usd(campaign.remaining_headroom_usd)],
    ['Active gate', gateLabel(campaign.active_gate)],
    ['Active blocker', campaign.active_blocker],
    ['Last activity', campaign.last_activity_at],
    ['Last receipt', campaign.last_receipt_id],
    ['Receipt hash', campaign.last_receipt_hash],
    ['Branch', campaign.branch],
    ['Integration SHA', campaign.integration_sha],
    ['Base ref', campaign.base_ref],
    ['Source authority', campaign.source_authority],
  ];
  return (
    <dl className="campaign-telemetry">
      {rows.map(([label, value]) => (
        <React.Fragment key={label}>
          <dt>{label}</dt>
          <dd>{shown(value)}</dd>
        </React.Fragment>
      ))}
    </dl>
  );
}

/** The server's provenance badge, verbatim; never derived in the browser. */
export function ProvenanceBadge({ provenance }) {
  if (!provenance || !provenance.badge) {
    return <span className="badge badge-provenance badge-provenance-missing">provenance not reported</span>;
  }
  return (
    <span
      className={'badge badge-provenance badge-provenance-' + shown(provenance.kind)}
      title={'workspace: ' + shown(provenance.workspace)}
    >
      {provenance.badge}
    </span>
  );
}

const HISTORY_STATUS_TEXT = {
  available: 'Imported history from the Owner History Index is included below.',
  none_recorded: 'The Owner History Index has no records for this campaign.',
  not_configured: 'No Owner History Index is configured on this server.',
  unavailable: 'The Owner History Index could not be read.',
};

/**
 * Live + imported evidence for one campaign, merged by the server. Every row
 * carries the badge of the source that reported it. An aspect that no source
 * reported is an explicit gap.
 */
export function MergedTimeline({ campaign }) {
  const timeline = campaign.timeline;
  if (!timeline) {
    return <p className="timeline-empty">The server did not report a timeline for this campaign.</p>;
  }
  const aspects = Array.isArray(timeline.aspects) ? timeline.aspects : [];
  const entries = Array.isArray(timeline.entries) ? timeline.entries : [];
  return (
    <div className="merged-timeline">
      <p className="timeline-history-note">
        {HISTORY_STATUS_TEXT[timeline.history_status] || NOT_REPORTED}
      </p>
      {timeline.withheld_records ? (
        <p className="timeline-withheld">
          {timeline.withheld_records} imported record(s) were withheld because their provenance
          could not be shown safely.
        </p>
      ) : null}
      <dl className="timeline-aspects">
        {aspects.map((aspect) => (
          <React.Fragment key={aspect.key}>
            <dt>{aspect.label}</dt>
            <dd>
              {aspect.reported && Array.isArray(aspect.items) && aspect.items.length > 0 ? (
                <ul>
                  {aspect.items.map((item, index) => (
                    <li key={index + ':' + item.badge + ':' + item.label}>
                      <span className="badge badge-provenance">{item.badge}</span>{' '}
                      {item.label}: {item.value}
                      {item.at ? <time> ({item.at})</time> : null}
                    </li>
                  ))}
                </ul>
              ) : (
                <span className="timeline-gap">{NOT_REPORTED}</span>
              )}
            </dd>
          </React.Fragment>
        ))}
      </dl>
      <h3>Timeline</h3>
      {entries.length === 0 ? (
        <p className="timeline-empty">No timeline entries were reported.</p>
      ) : (
        <ol className="timeline-entries">
          {entries.map((entry, index) => (
            <li key={index + ':' + entry.badge + ':' + entry.at + ':' + entry.kind}>
              <time>{shown(entry.at)}</time>{' '}
              <span className="badge badge-provenance">{entry.badge}</span> <span>{entry.message}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

/** Origin + source-authority badges. `source_local` is REQUIRED-visible. */
export function CampaignBadges({ campaign }) {
  return (
    <>
      <ProvenanceBadge provenance={campaign.provenance} />
      {/* REQUIRED: source-local status is visible, never hidden. */}
      {campaign.source_local ? (
        <span className="badge badge-source-local">Source-local</span>
      ) : (
        <span className="badge badge-cloud">Cloud</span>
      )}
      <span className="badge badge-authority">{shown(campaign.source_authority)}</span>
    </>
  );
}

/** The server's honest note for a workspace it could not read. */
export function DiagnosticNote({ campaign }) {
  if (!campaign.diagnostic_note) return null;
  return (
    <p className="unavailable diagnostic-note" role="status">
      Unavailable — {campaign.diagnostic_note}
    </p>
  );
}

function CampaignsPage() {
  const [campaigns, setCampaigns] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    setError(null);
    try {
      const response = await fetchCampaigns();
      setCampaigns(response.campaigns || []);
    } catch (err) {
      if (err instanceof OperationsApiError && err.isUnavailable) {
        setCampaigns([]);
        setError('unavailable');
      } else {
        setCampaigns([]);
        setError(err instanceof Error ? err.message : 'the campaign list could not be loaded');
      }
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const act = useCallback(
    async (campaignId, verb) => {
      setBusy(campaignId + ':' + verb);
      try {
        if (verb === 'pause') await pauseCampaign(campaignId);
        else if (verb === 'resume') await resumeCampaign(campaignId);
        else if (verb === 'stop') await stopCampaign(campaignId);
        await load();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'the action failed');
      } finally {
        setBusy('');
      }
    },
    [load],
  );

  if (error === 'unavailable') {
    return (
      <section>
        <h1>Campaigns</h1>
        <p className="unavailable">
          Unavailable — the operations backend is not mounted for this deployment, so no
          campaign can be listed. Nothing here is fabricated.
        </p>
      </section>
    );
  }

  if (campaigns === null) {
    return (
      <section>
        <h1>Campaigns</h1>
        <p>Loading…</p>
      </section>
    );
  }

  return (
    <section>
      <h1>Campaigns</h1>
      {error ? <p className="error">{error}</p> : null}
      {campaigns.length === 0 ? <p>No campaigns were reported by the server.</p> : null}
      <ul className="campaign-list">
        {campaigns.map((campaign) => (
          <li
            key={(campaign.workspace_id || '') + ':' + campaign.campaign_id}
            className={campaign.diagnostic_note ? 'campaign-card campaign-card-degraded' : 'campaign-card'}
          >
            <h2>
              <Link href={'/campaigns/' + encodeURIComponent(campaign.campaign_id)}>
                {campaign.title}
              </Link>
              <span className="badge">{campaign.status}</span>
              <CampaignBadges campaign={campaign} />
            </h2>
            <p>Updated {shown(campaign.updated_at)}</p>
            <DiagnosticNote campaign={campaign} />
            <CampaignTelemetry campaign={campaign} />
            <h3>Recent events</h3>
            <EventTail events={campaign.live_event_tail} />
            {campaign.provenance && campaign.provenance.kind === 'imported' ? (
              <p className="imported-read-only">
                Imported history is read-only; lifecycle controls are not available for it.
              </p>
            ) : (
              <div className="campaign-controls">
                <button
                  type="button"
                  disabled={busy !== ''}
                  onClick={() => act(campaign.campaign_id, 'pause')}
                >
                  Pause
                </button>
                <button
                  type="button"
                  disabled={busy !== ''}
                  onClick={() => act(campaign.campaign_id, 'resume')}
                >
                  Resume
                </button>
                <button
                  type="button"
                  disabled={busy !== ''}
                  onClick={() => act(campaign.campaign_id, 'stop')}
                >
                  Stop
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

export default CampaignsPage;
